'use client';

import { c as _c } from "react/compiler-runtime";
import type { Ref } from 'react';
import { Image } from '@/app/components/media/image';
import { generateSources } from '@/app/components/media/image/generate-sources';
import { type ImageProps } from '@/app/components/media/image/types';
import styles from '@/app/cv/components/sections/home/style.module.css';
import { cn } from '@/app/utils/cn';
import { createDataAttributes } from '@/app/utils/createDataAttributes';
export type CubeAnimateType = 'idle' | 'random' | 'interactive';
const faceVariantClass = {
  front: styles.lineUpCubeFaceFront,
  back: styles.lineUpCubeFaceBack,
  left: styles.lineUpCubeFaceLeft,
  right: styles.lineUpCubeFaceRight,
  top: styles.lineUpCubeFaceTop,
  bottom: styles.lineUpCubeFaceBottom
} as const;
export interface LineupCubeProps {
  cube: {
    image: Omit<ImageProps, 'aspectRatio'>;
    side: 'top' | 'bottom' | 'left' | 'right' | 'front' | 'back';
  }[];
  animate?: CubeAnimateType;
  className?: string;
  triggerFade?: () => void;
  cubeRef?: Ref<HTMLDivElement>;
  cubeInnerRef?: Ref<HTMLDivElement>;
}
export const LineupCube = t0 => {
  const $ = _c(20);
  const {
    cubeRef,
    cube,
    cubeInnerRef,
    animate: t1,
    className,
    triggerFade
  } = t0;
  const animate = t1 === undefined ? "idle" : t1;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <div className={cn("center-x absolute h-auto w-screen", styles.lineUpCubeOverlayTop, "before:absolute before:bottom-full before:left-0 before:w-full", "after:absolute after:left-0 after:max-h-125 after:min-h-105 after:w-full after:bg-white")} />;
    $[0] = t2;
  } else {
    t2 = $[0];
  }
  const t3 = animate === "interactive";
  let t4;
  if ($[1] !== t3) {
    t4 = cn(styles.lineUpCubeStyle, {
      [styles.animateInteractive]: t3
    });
    $[1] = t3;
    $[2] = t4;
  } else {
    t4 = $[2];
  }
  let t5;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = createDataAttributes({
      test: "cube"
    });
    $[3] = t5;
  } else {
    t5 = $[3];
  }
  let t6;
  if ($[4] !== className) {
    t6 = cn(styles.lineUpCubeWrap, className);
    $[4] = className;
    $[5] = t6;
  } else {
    t6 = $[5];
  }
  let t7;
  if ($[6] !== cube) {
    t7 = cube.map(_temp);
    $[6] = cube;
    $[7] = t7;
  } else {
    t7 = $[7];
  }
  let t8;
  if ($[8] !== cubeInnerRef || $[9] !== t6 || $[10] !== t7 || $[11] !== triggerFade) {
    t8 = <div className={t6} ref={cubeInnerRef} onAnimationEnd={triggerFade}>{t7}</div>;
    $[8] = cubeInnerRef;
    $[9] = t6;
    $[10] = t7;
    $[11] = triggerFade;
    $[12] = t8;
  } else {
    t8 = $[12];
  }
  let t9;
  if ($[13] !== cubeRef || $[14] !== t4 || $[15] !== t8) {
    t9 = <div className="absolute inset-0 m-auto"><div ref={cubeRef} className={t4} data-cube={true} {...t5}>{t8}</div></div>;
    $[13] = cubeRef;
    $[14] = t4;
    $[15] = t8;
    $[16] = t9;
  } else {
    t9 = $[16];
  }
  let t10;
  if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = <div className={cn("center-x pointer-events-none absolute h-auto w-screen", styles.lineUpCubeOverlayBottom, "before:absolute before:bottom-full before:left-0 before:aspect-1440/178 before:w-full", "after:absolute after:top-0 after:left-0 after:w-full")} />;
    $[17] = t10;
  } else {
    t10 = $[17];
  }
  let t11;
  if ($[18] !== t9) {
    t11 = <div className="pointer-events-none absolute top-0 left-0 w-full"><div className="px-6 sm:px-12 lg:px-0"><div className="aspect-4-3 relative mx-auto w-full max-w-144">{t2}{t9}{t10}</div></div></div>;
    $[18] = t9;
    $[19] = t11;
  } else {
    t11 = $[19];
  }
  return t11;
};
function _temp(item, index) {
  return <div key={index} className={cn(styles.lineUpCubeFace, faceVariantClass[item.side])}><Image rootMargin="100% 0px 0px 0px" src={item.image.src} alt={item.image.alt} width={item.image.width} height={item.image.height} aspectRatio="aspect-1-1" sources={[{
      srcset: item.image.src,
      media: {
        minWidth: 480
      },
      options: {
        width: 640,
        crop: "1:1"
      }
    }, ...generateSources("mobile", {
      mobileSrc: item.image.src,
      mobileOptions: {
        crop: "1:1"
      }
    })]} /></div>;
}